import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import NexLogo from "Assets/Admin/NexLogo.svg";

import { HTTPErrorCallback } from "Helpers/Error";

import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import CaptchaWidget from "Components/Common/CaptchaWidget";
import FormWrapper from "Components/Common/FormWrapper";
import Button from "Components/Global/Buttons";
import Input from "Components/Global/Form/Input";

import * as S from "./styles";

const schema = z
  .object({
    password: z
      .string()
      .min(8, "A senha deve ter no mínimo 8 caracteres")
      .regex(/[A-Z]/, "A senha deve conter ao menos uma letra maiúscula")
      .regex(/[a-z]/, "A senha deve conter ao menos uma letra minúscula")
      .regex(/[0-9]/, "A senha deve conter ao menos um número"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "As senhas não coincidem",
    path: ["confirmPassword"],
  });

const ChangePassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api } = UseServices();
  const { showNotification } = UseNotification();

  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [reCaptchaToken, setReCaptchaToken] = useState(null);

  const methods = useForm({
    resolver: zodResolver(schema),
  });

  const { register } = methods;

  useEffect(() => {
    validateToken();
    // eslint-disable-next-line
  }, []);

  const validateToken = async () => {
    const token = searchParams.get("token");

    if (!token) {
      showNotification(
        "danger",
        "Atenção!",
        "Token não fornecido. Por favor, use o link enviado por e-mail.",
      );
      navigate("/admin");
      return;
    }

    try {
      setLoading(true);
      await api.post(
        "/sessions/password/validate-token",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setTokenValid(true);
      setLoading(false);
    } catch (error) {
      HTTPErrorCallback(error);
      setTimeout(() => navigate("/admin"), 3000);
    }
  };

  const onSubmit = async (data) => {
    if (!reCaptchaToken) {
      showNotification(
        "warning",
        "Atenção!",
        "É necessário confirmar que você não é um robô.",
      );
      return;
    }

    setLoading(true);

    const token = searchParams.get("token");

    try {
      await api.post(
        "/sessions/password/change",
        {
          password: data.password,
          captcha_token: reCaptchaToken,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      showNotification(
        "success",
        "Sucesso!",
        "Senha alterada com sucesso! Redirecionando...",
      );

      setTimeout(() => navigate("/home"), 2000);
    } catch (error) {
      setLoading(false);
      HTTPErrorCallback(error);
    }
  };

  if (!tokenValid && !loading) {
    return null;
  }

  return (
    <S.Container>
      <S.Content>
        <S.LogoWrapper>
          <img src={NexLogo} alt="SevenX Logo" />
        </S.LogoWrapper>

        <S.Title>Redefinir Senha</S.Title>
        <S.Subtitle>
          Digite sua nova senha abaixo. Ela deve conter no mínimo 8 caracteres,
          incluindo letras maiúsculas, minúsculas e números.
        </S.Subtitle>

        {loading ? (
          <S.LoadingMessage>Validando token...</S.LoadingMessage>
        ) : (
          <FormWrapper {...methods} submit={onSubmit}>
            <Input
              name="password"
              label="Nova Senha"
              type="password"
              placeholder="Digite sua nova senha"
              {...register("password")}
            />

            <Input
              name="confirmPassword"
              label="Confirmar Nova Senha"
              type="password"
              placeholder="Digite novamente sua nova senha"
              {...register("confirmPassword")}
            />

            <CaptchaWidget onSuccess={setReCaptchaToken} />

            <S.ButtonWrapper>
              <Button
                type="submit"
                disabled={loading}
                fullWidth
                text="Redefinir Senha"
              />
            </S.ButtonWrapper>

            <S.BackLink onClick={() => navigate("/admin")}>
              Voltar para o login
            </S.BackLink>
          </FormWrapper>
        )}
      </S.Content>
    </S.Container>
  );
};

export default ChangePassword;
